'use client';

import { RefObject, useCallback, useEffect } from 'react';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { useEnv } from '@/env/client';

interface IGenericHtmlFormsProps {
  containerRef: RefObject<HTMLDivElement>;
}

// Generate a truly unique ID for forms
const generateUniqueFormId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `form_${crypto.randomUUID()}`;
  }
  // Fallback for older browsers
  return `form_${Date.now()}`;
};

export const GenericHtmlForms = ({ containerRef }: IGenericHtmlFormsProps) => {
  const { env } = useEnv();
  const { hasError, verifyCaptcha } = useRecaptcha();

  const handleFormSubmit = useCallback(async (event: Event) => {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const service = form.dataset.service?.trim();

    const elementThankYou = form.querySelector('[data-thank-you]') as HTMLElement | null;
    const elementCaptchaError = form.querySelector('[data-captcha-error]') as HTMLElement | null;

    if (!service) {
      console.warn('Generic form submission attempted without a data-service attribute.');
      return;
    }

    const formId = form.getAttribute('id') || generateUniqueFormId();
    const formData = new FormData(form);

    form.style.opacity = '0.5';
    form.style.pointerEvents = 'none';

    if (env.ENABLE_FORMS_CAPTCHA) {
      const captchaResponse = await verifyCaptcha(env.RECAPTCHA_SITE_KEY, `submit__${formId}`);

      if (hasError || !captchaResponse?.success) {
        if (elementCaptchaError) {
          elementCaptchaError.style.display = 'block';
        }
        form.style.opacity = '1';
        form.style.pointerEvents = 'auto';
        return;
      }

      form.style.opacity = '1';
      form.style.pointerEvents = 'auto';
    }

    formData.append('_service', service);

    const response = await fetch('/api/forms/submitGenericHtmlForm', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();

    if (result.success) {
      if (result.redirectUrl) {
        globalThis.location.href = result.redirectUrl;
        return;
      }

      if (elementThankYou) {
        elementThankYou.style.display = 'block';
      }
    } else {
      console.error('Form submission failed:', result.error);
    }

    form.style.opacity = '1';
    form.style.pointerEvents = 'auto';
  }, []);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const forms = containerRef.current.querySelectorAll<HTMLFormElement>('form[data-service]:not([data-service=""])');

    if (forms.length === 0) {
      return;
    }

    // Add event listeners
    forms.forEach((form) => {
      form.dataset.submitControlled = 'true';
      form.addEventListener('submit', handleFormSubmit);
    });

    // Cleanup: remove event listeners
    return () => {
      forms.forEach((form) => {
        form.removeEventListener('submit', handleFormSubmit);
      });
    };
  }, [containerRef, handleFormSubmit]);

  return null;
};
